import React, { useCallback, useEffect, useState } from 'react'
import { Accordion, Col, Row } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import moment from 'moment-timezone'

import useApi from '../../hooks/useApi'
import SortTable from '../../components/sortTable/sortTable'
import { feeKind, feeValue, formatAmount } from '../../lib/numbers'
import { downloadCSV } from '../../lib/files'
import Icon from '../../components/icon'
import civ from '../../constants/civ'

export default function CivillosWallet({ setLoading }) {
  const { t } = useTranslation()
  const [transactions, setTransactions] = useState([])
  const [totalAmount, setTotalAmount] = useState(0)
  const [queryParams, setQueryParams] = useState({
    sort_by: 'created_at',
    sort_order: 'DESC',
  })

  const { getCivillosWallet } = useApi()

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const response = await getCivillosWallet(queryParams)
      setTransactions(
        response.data?.map(d => ({
          created_at: moment(d.created_at).format(t('config.date_format')),
          purchaser_name: d.purchaser_name,
          amount: formatAmount(d.amount),
          admin_fee_value: formatAmount(
            feeValue(d.amount, {
              fee_kind: d.civillos_fee_kind,
              value: d.civillos_admin_fee_value,
            }),
            true,
          ),
          admin_fee_amount: feeKind({
            fee_kind: d.civillos_fee_kind,
            value: d.civillos_admin_fee_value,
          }),
        })),
      )

      setTotalAmount(
        response.data.reduce((acc, obj) => {
          return (
            acc +
            feeValue(obj.amount, {
              fee_kind: obj.civillos_fee_kind,
              value: obj.civillos_admin_fee_value,
            })
          )
        }, 0),
      )
    } catch (error) {
      console.log(error)
    }
    setLoading(false)
  }, [getCivillosWallet, queryParams])

  useEffect(() => {
    fetchData()
  }, [queryParams])

  const headers = [
    {
      title: t('accounting.civillos.headers.date'),
      prop: 'created_at',
      isSortable: true,
    },
    {
      title: t('accounting.civillos.headers.username'),
      prop: 'purchaser_name',
      isSortable: true,
    },
    {
      title: t('accounting.civillos.headers.civ_amount'),
      prop: 'amount',
      isSortable: true,
    },
    {
      title: t('accounting.civillos.headers.fee_value'),
      prop: 'admin_fee_value',
      isSortable: true,
    },
    {
      title: t('accounting.civillos.headers.admin_fee'),
      prop: 'admin_fee_amount',
      isSortable: true,
    },
  ]

  const onFetch = params => {
    if (!!params.sortState.prop) {
      setQueryParams({
        sort_by: params.sortState.prop,
        sort_order: params.sortState.order.toUpperCase(),
      })
    }
  }

  const downloadReport = () => {
    setLoading(true)
    getCivillosWallet(queryParams, 'csv')
      .then(response => {
        const fileName = `civillos_report_${moment().format(
          t('config.file_name_date_format'),
        )}.csv`
        downloadCSV(response.data, fileName)
        setLoading(false)
      })
      .catch(error => {
        console.error(error)
        setLoading(false)
      })
  }

  return (
    <Accordion.Item eventKey="civillosWallet" className="border event-pledge">
      <Accordion.Header className="border-top">
        <Row className="w-100 justify-content-between align-items-center">
          <Col className="text-start fw-semibold" lg="4">
            {t('accounting.civillos.title')}
          </Col>
          <Col className="text-end pe-4">
            {transactions?.length ? (
              <>
                <span>
                  {t('accounting.civillos.totalAmount', {
                    total: formatAmount(totalAmount, true),
                  })}
                </span>
                <div
                  className="btn btn-link p-0 text-decoration-none"
                  onClick={() => downloadReport()}
                >
                  {t('accounting.civillos.downloadReport.label')}
                  <Icon name="download" />
                </div>
              </>
            ) : null}
          </Col>
        </Row>
      </Accordion.Header>
      <Accordion.Body className="p-0">
        <SortTable
          body={transactions}
          headers={headers}
          onFetch={s => onFetch(s)}
          showPagination={false}
        />
      </Accordion.Body>
    </Accordion.Item>
  )
}
